import demoCalendarMonthlyDark from "../../assets/screenshots/demo-calendar-monthly-dark-110.png";
import demoCalendarMonthlyLight from "../../assets/screenshots/demo-calendar-monthly-light-110.png";
import demoCalendarTimelineDark from "../../assets/screenshots/demo-calendar-timeline-dark-110.png";
import demoCalendarTimelineLight from "../../assets/screenshots/demo-calendar-timeline-light-110.png";
import demoCalendarWeeklyDark from "../../assets/screenshots/demo-calendar-weekly-dark-110.png";
import demoCalendarWeeklyLight from "../../assets/screenshots/demo-calendar-weekly-light-110.png";
import demoEditorDark from "../../assets/screenshots/demo-editor-dark-110.png";
import demoEditorLight from "../../assets/screenshots/demo-editor-light-110.png";
import demoSettingsGeneralDark from "../../assets/screenshots/demo-settings-general-dark-110.png";
import demoSettingsGeneralLight from "../../assets/screenshots/demo-settings-general-light-110.png";
import demoSettingsPresetsDark from "../../assets/screenshots/demo-settings-presets-dark-110.png";
import demoSettingsPresetsLight from "../../assets/screenshots/demo-settings-presets-light-110.png";
import demoTableDark from "../../assets/screenshots/demo-table-dark-110.png";
import demoTableLight from "../../assets/screenshots/demo-table-light-110.png";
import type {
  LandingContent,
  OpticCarouselSlide,
  OpticComparisonColumn,
  OpticComparisonRow,
  OpticFaqItem,
  OpticHowItWorksStep,
  OpticPricingTier,
  OpticSpotlightItem,
  OpticStatistic,
  OpticTestimonial,
} from "./landing.types";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type ScreenshotAsset = {
  src: string;
  alt: string;
  caption?: string;
};

export type ThemedScreenshotAsset = {
  light: string;
  dark: string;
  alt: string;
  caption?: string;
};

export type WorkflowSectionData = {
  id: string;
  label: string;
  title: string;
  summary: string;
  steps: string[];
  screenshot: ThemedScreenshotAsset;
};

export type FeatureScreenshot = {
  title: string;
  caption: string;
  screenshot: ThemedScreenshotAsset;
};

export type CalendarCarouselSlide = {
  id: string;
  title: string;
  description: string;
  screenshot: ThemedScreenshotAsset;
};

export type Plan = {
  id: "free" | "pro";
  name: string;
  price: string;
  period: string;
  features: string[];
  cta: string;
  to: string;
  highlighted?: boolean;
};

export type ComparisonRow = {
  feature: string;
  support: Record<ToolId, boolean>;
};

export type OpticNavLink = {
  href: string;
  label: string;
};

export type OpticBenchmarkRowVariant = "featured" | "default" | "muted";

export type OpticBenchmarkRow = {
  engine: string;
  write: string;
  memory: string;
  result: string;
  variant: OpticBenchmarkRowVariant;
  strikeWrite?: boolean;
  strikeMemory?: boolean;
  dangerMemory?: boolean;
  dangerResult?: boolean;
};

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

export const CONTAINER_CLASS =
  "mx-auto w-full max-w-[1440px] px-6 sm:px-8 lg:px-12";
export const LAUNCH_EDITOR_CTA = "Launch Editor (Free)";

export const OPTIC_HEADER_BRAND = "EX/FTRATOR";

export const OPTIC_NAV_LINKS: OpticNavLink[] = [
  { href: "#how-it-works", label: "How It Works" },
  { href: "#spotlight", label: "Features" },
  { href: "#statistics", label: "Stats" },
  { href: "#carousel", label: "Workflow" },
  { href: "#comparison", label: "Comparison" },
  { href: "#pricing", label: "Pricing" },
  { href: "#faq", label: "FAQ" },
];

export const OPTIC_HEADER_CTA = {
  label: "Download",
  to: "/app",
};

export const OPTIC_HERO = {
  headline: "10k RAWs. 2.4s.",
  highlight: "Absolute Control.",
  description:
    "The zero-latency EXIF/IPTC editor designed for media ops. Write directly to file headers. Stop waiting on bloated UI threads.",
  primaryCta: {
    label: "Download for macOS",
    to: "/app",
  },
  secondaryCta: {
    label: "View Benchmarks",
    to: "#specs",
  },
  frameWatermark: "AWAITS",
  frameLabel: "[ High-Speed UI Stream Processing ]",
};

export const OPTIC_SPECS = {
  headingLead: "Built for the",
  headingHighlight: "50-Terabyte Ingest.",
  eyebrow: "Direct-to-Disk Writes",
  title: "Traditional tools crash. First-party apps strip proprietary tags.",
  summary:
    "Exiftrator gives you CLI precision with a specialized visual interface. Master full libraries of CR3, ARW, DNG, and sidecar XMP with total architectural safety.",
};

export const OPTIC_BENCHMARK_HEADERS = [
  "Engine",
  "10k Write",
  "Memory",
  "Result",
] as const;

export const OPTIC_BENCHMARK_ROWS: OpticBenchmarkRow[] = [
  {
    engine: "Exiftrator V2",
    write: "~2.4s",
    memory: "120 MB",
    result: "[ SUCCESS ]",
    variant: "featured",
  },
  {
    engine: "ExifTool (CLI)",
    write: "~18.0s",
    memory: "85 MB",
    result: "[ NO UI ]",
    variant: "default",
  },
  {
    engine: "Adobe Bridge",
    write: "Timeout",
    memory: "4.2 GB",
    result: "[ CRASH ]",
    variant: "muted",
    strikeWrite: true,
    strikeMemory: true,
    dangerMemory: true,
    dangerResult: true,
  },
];

export const OPTIC_SECURITY = {
  title: "Byte-Level Writes.",
  subtitle: "Zero Data Corruption.",
  description:
    "Built over a powerful Rust core. Designed to operate completely offline. Your proprietary assets never leave your SSD, safeguarded by automatic byte-for-byte rollback transaction logs.",
  diagramLabel: "< Diagram: Rust Core File I/O >",
};

export const OPTIC_FINAL_CTA = {
  title: "Stop babysitting loading bars.",
  description:
    "14-day fully unlocked access. No credit card required. Installs in 3 seconds. Local-only architecture guarantees your unreleased assets stay yours.",
  button: {
    label: "Initialize Download (34MB)",
    to: "/app",
  },
};

export const OPTIC_FOOTER_LINKS = [
  { href: "/privacy", label: "Privacy" },
  { href: "/terms", label: "Terms" },
];

export const NAV_LINKS = [
  { href: "#workflows", label: "Workflows" },
  { href: "#features", label: "Features" },
  { href: "#comparison", label: "Comparison" },
  { href: "#pricing", label: "Pricing" },
  { href: "#faq", label: "FAQ" },
];

export const CALENDAR_WORKFLOW = {
  label: "Scanned Archive",
  title: "Fix wrong scan dates with calendar tools",
  summary:
    "Use one visual surface for timeline moves, day placement, and weekly correction context.",
  steps: [
    "Drop scans",
    "Switch views",
    "Drag and confirm",
    "Export corrected dates",
  ],
};

export const HERO_SCREENSHOT: ThemedScreenshotAsset = {
  light: demoEditorLight,
  dark: demoEditorDark,
  alt: "Exiftrator editor view with metadata fields, map preview, and selected photo list.",
};

export const CALENDAR_CAROUSEL_SLIDES: CalendarCarouselSlide[] = [
  {
    id: "timeline",
    title: "Timeline View",
    description:
      "Drag photo clusters directly across time for fast date correction.",
    screenshot: {
      light: demoCalendarTimelineLight,
      dark: demoCalendarTimelineDark,
      alt: "Exiftrator timeline calendar mode with draggable photo clusters in light and dark themes.",
    },
  },
  {
    id: "calendar",
    title: "Calendar View",
    description: "Place photos on exact days in a classic monthly grid.",
    screenshot: {
      light: demoCalendarMonthlyLight,
      dark: demoCalendarMonthlyDark,
      alt: "Exiftrator monthly calendar mode with photo thumbnails placed by day in light and dark themes.",
    },
  },
  {
    id: "weekly",
    title: "Weekly View",
    description: "Fine-tune timestamps inside a focused week schedule.",
    screenshot: {
      light: demoCalendarWeeklyLight,
      dark: demoCalendarWeeklyDark,
      alt: "Exiftrator weekly calendar mode with hour-by-hour scheduling in light and dark themes.",
    },
  },
];

export const OTHER_WORKFLOWS: WorkflowSectionData[] = [
  {
    id: "workflow-photographer",
    label: "Working Photographer",
    title: "Standardize shoot metadata before delivery",
    summary: "Batch-apply metadata fields and verify key files quickly.",
    steps: ["Filter in Table", "Batch apply values", "Spot-check in Inspector"],
    screenshot: {
      light: demoTableLight,
      dark: demoTableDark,
      alt: "Exiftrator table view showing batch metadata editing across multiple images.",
    },
  },
  {
    id: "workflow-cleanup",
    label: "Library Cleanup",
    title: "Normalize mixed camera libraries",
    summary:
      "Find outliers, resolve metadata drift, and ship a clean export set.",
    steps: ["Find outliers", "Correct across views", "Finalize export"],
    screenshot: {
      light: demoEditorLight,
      dark: demoEditorDark,
      alt: "Exiftrator inspector and editor view used to clean and standardize metadata values.",
    },
  },
];

export const FEATURE_SCREENSHOTS: FeatureScreenshot[] = [
  {
    title: "Location Search",
    caption: "Search places and refine GPS coordinates on an interactive map.",
    screenshot: {
      light: demoEditorLight,
      dark: demoEditorDark,
      alt: "Exiftrator editor with location search field and interactive map context panel.",
    },
  },
  {
    title: "Presets",
    caption: "Save reusable metadata sets and apply them across large batches.",
    screenshot: {
      light: demoSettingsPresetsLight,
      dark: demoSettingsPresetsDark,
      alt: "Exiftrator presets settings screen for creating and managing metadata presets.",
    },
  },
  {
    title: "Undo / Redo",
    caption:
      "Track every change with full session history and instant rollback.",
    screenshot: {
      light: demoTableLight,
      dark: demoTableDark,
      alt: "Exiftrator table workflow where undo and redo controls are available in the app header.",
    },
  },
  {
    title: "Configuration Import / Export",
    caption: "Move metadata configurations between projects and devices.",
    screenshot: {
      light: demoSettingsGeneralLight,
      dark: demoSettingsGeneralDark,
      alt: "Exiftrator general settings with import and export configuration actions.",
    },
  },
];

export const PLANS: Plan[] = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    period: "forever",
    features: [
      "Table, Calendar, and Inspector views",
      "Core EXIF editing for JPEG",
      "GPS tagging and map lookup",
      "Undo/redo history",
    ],
    cta: LAUNCH_EDITOR_CTA,
    to: "/app",
  },
  {
    id: "pro",
    name: "Pro",
    price: "$39",
    period: "/year",
    features: [
      "Everything in Free",
      "Desktop app with offline mode",
      "Advanced tags and templates",
      "Preset-based workflows",
    ],
    cta: "Coming Soon",
    to: "",
    highlighted: true,
  },
];

export const FAQ = [
  {
    question: "Do I need to pick only one workflow?",
    answer:
      "No. Workflow sections are starting points. You can switch between table, calendar, and inspector views in the same session.",
  },
  {
    question: "What formats can I write metadata to?",
    answer:
      "Exiftrator currently writes metadata to JPEG files. PNG, TIFF, HEIC, and WebP can be imported for preview. Support for additional write formats is on the roadmap.",
  },
  {
    question: "Is processing local?",
    answer:
      "Yes. All processing happens entirely in your browser. Files never leave your device. The only external requests are GPS lookups to OpenStreetMap's Nominatim service when you use location search.",
  },
  {
    question: "What happens to my original files?",
    answer:
      "Your originals are never modified. Exiftrator works on in-memory copies and produces new files on export. You always keep your untouched source files.",
  },
  {
    question: "Which browsers are supported?",
    answer:
      "Exiftrator works in all modern browsers: Chrome, Firefox, Safari, and Edge. For the best experience we recommend a Chromium-based browser (Chrome or Edge) on desktop.",
  },
  {
    question: "Is there a file size or batch limit?",
    answer:
      "There is no hard limit. Performance depends on your device's memory. Most users comfortably edit batches of 50–200 photos at a time. Very large batches may slow down on low-memory devices.",
  },
  {
    question: "Does it work on mobile?",
    answer:
      "The web app is responsive, but metadata editing is most practical on a desktop or tablet screen where you can see the table and calendar views side by side.",
  },
  {
    question: "Are these real product screenshots?",
    answer:
      "Yes. The landing page uses live screenshots captured from the actual app.",
  },
];

export const PREVIEW_FIELDS = [
  "dateTaken",
  "artist",
  "copyright",
  "cameraMake",
  "cameraModel",
  "lensModel",
  "focalLength",
  "aperture",
  "shutterSpeed",
  "iso",
  "gpsLatitude",
  "gpsLongitude",
];

export const FIELD_LABELS: Record<string, string> = {
  dateTaken: "Date Taken",
  artist: "Artist",
  copyright: "Copyright",
  cameraMake: "Camera Make",
  cameraModel: "Camera Model",
  lensModel: "Lens Model",
  focalLength: "Focal Length",
  aperture: "Aperture",
  shutterSpeed: "Shutter Speed",
  iso: "ISO",
  gpsLatitude: "GPS Latitude",
  gpsLongitude: "GPS Longitude",
};

export const COMPARISON_TOOLS = [
  {
    id: "exiftrator",
    name: "Exiftrator",
    label: "Free / $39yr",
    featured: true,
  },
  { id: "exiftool", name: "ExifTool", label: "Free CLI" },
  { id: "lightroom", name: "Lightroom", label: "$10+/mo" },
  { id: "photomechanic", name: "Photo Mechanic", label: "Paid" },
  { id: "webtools", name: "Single-file Web Tools", label: "Varies" },
] as const;

export type ToolId = (typeof COMPARISON_TOOLS)[number]["id"];

export const COMPARISON_ROWS: ComparisonRow[] = [
  {
    feature: "Visual table batching",
    support: {
      exiftrator: true,
      exiftool: false,
      lightroom: false,
      photomechanic: false,
      webtools: false,
    },
  },
  {
    feature: "Calendar timeline correction",
    support: {
      exiftrator: true,
      exiftool: false,
      lightroom: false,
      photomechanic: false,
      webtools: false,
    },
  },
  {
    feature: "No account required",
    support: {
      exiftrator: true,
      exiftool: true,
      lightroom: false,
      photomechanic: true,
      webtools: true,
    },
  },
  {
    feature: "Local-only browser workflow",
    support: {
      exiftrator: true,
      exiftool: true,
      lightroom: false,
      photomechanic: false,
      webtools: true,
    },
  },
  {
    feature: "Inspector + table + calendar in one app",
    support: {
      exiftrator: true,
      exiftool: false,
      lightroom: false,
      photomechanic: false,
      webtools: false,
    },
  },
  {
    feature: "RAW / HEIC / TIFF write support",
    support: {
      exiftrator: false,
      exiftool: true,
      lightroom: true,
      photomechanic: true,
      webtools: false,
    },
  },
  {
    feature: "Script-level metadata depth",
    support: {
      exiftrator: false,
      exiftool: true,
      lightroom: false,
      photomechanic: false,
      webtools: false,
    },
  },
  {
    feature: "Bulk file rename from metadata",
    support: {
      exiftrator: false,
      exiftool: true,
      lightroom: true,
      photomechanic: true,
      webtools: false,
    },
  },
  {
    feature: "Built for full photo catalog editing",
    support: {
      exiftrator: false,
      exiftool: false,
      lightroom: true,
      photomechanic: false,
      webtools: false,
    },
  },
  {
    feature: "Fast single-file metadata peek",
    support: {
      exiftrator: true,
      exiftool: false,
      lightroom: false,
      photomechanic: false,
      webtools: true,
    },
  },
];

/* ------------------------------------------------------------------ */
/*  Optic Modular Sections (Landing-only)                             */
/* ------------------------------------------------------------------ */

export const OPTIC_STATISTICS: OpticStatistic[] = [
  { value: "48,000+", label: "Images edited" },
  { value: "100%", label: "Local processing" },
  { value: "0", label: "Cloud file copies" },
  { value: "50-200", label: "Typical batch size" },
];

export const OPTIC_HOW_IT_WORKS_STEPS: OpticHowItWorksStep[] = [
  {
    title: "Drop photos",
    description:
      "Import your files directly from disk. No account and no upload queue.",
  },
  {
    title: "Edit in visual workflows",
    description:
      "Use timeline, calendar, table, and inspector views to correct metadata quickly.",
  },
  {
    title: "Export corrected files",
    description:
      "Keep originals untouched and export updated files with clean metadata.",
  },
];

export const OPTIC_SPOTLIGHT_ITEMS: OpticSpotlightItem[] = [
  {
    badge: "Calendar + Timeline",
    title: "Correct wrong scan dates visually",
    description:
      "Drag clusters on a timeline, then place photos on exact days and hours.",
    bullets: [],
    screenshot: {
      light: demoCalendarTimelineLight,
      dark: demoCalendarTimelineDark,
      alt: "Timeline view with draggable photo clusters.",
    },
  },
  {
    badge: "Batch Table",
    title: "Apply metadata across many files at once",
    description:
      "Filter, multi-select, and batch-edit key EXIF fields in one pass.",
    bullets: [],
    screenshot: {
      light: demoTableLight,
      dark: demoTableDark,
      alt: "Table view for bulk metadata editing.",
    },
  },
  {
    badge: "Inspector + Map",
    title: "Tag locations with map-backed search",
    description:
      "Search places, review coordinates, and write precise GPS metadata.",
    bullets: [],
    screenshot: {
      light: demoEditorLight,
      dark: demoEditorDark,
      alt: "Editor view with map and metadata fields.",
    },
  },
  {
    badge: "Presets",
    title: "Reuse metadata templates across projects",
    description:
      "Save presets once and apply consistent metadata values to each new batch.",
    bullets: [],
    screenshot: {
      light: demoSettingsPresetsLight,
      dark: demoSettingsPresetsDark,
      alt: "Preset management view with reusable metadata templates.",
    },
  },
];

export const OPTIC_PRODUCT_CAROUSEL_SLIDES: OpticCarouselSlide[] = [
  {
    id: "timeline",
    title: "Timeline for large date shifts",
    description:
      "Move photo clusters directly through time for high-volume correction.",
    screenshot: {
      light: demoCalendarTimelineLight,
      dark: demoCalendarTimelineDark,
      alt: "Exiftrator timeline view screenshot.",
    },
  },
  {
    id: "calendar-month",
    title: "Monthly calendar placement",
    description:
      "Drop files onto exact days when batch scans share rough ranges.",
    screenshot: {
      light: demoCalendarMonthlyLight,
      dark: demoCalendarMonthlyDark,
      alt: "Exiftrator monthly calendar view screenshot.",
    },
  },
  {
    id: "calendar-week",
    title: "Weekly time-level refinement",
    description: "Fine tune hours and sequencing when event order matters.",
    screenshot: {
      light: demoCalendarWeeklyLight,
      dark: demoCalendarWeeklyDark,
      alt: "Exiftrator weekly calendar view screenshot.",
    },
  },
  {
    id: "presets",
    title: "Preset-powered repeat workflows",
    description:
      "Save metadata templates and reapply for consistent delivery standards.",
    screenshot: {
      light: demoSettingsPresetsLight,
      dark: demoSettingsPresetsDark,
      alt: "Exiftrator presets management screenshot.",
    },
  },
];

export const OPTIC_TESTIMONIALS: OpticTestimonial[] = [
  {
    quote:
      "I cleaned ten years of scanned family photos in one weekend because timeline drag + calendar placement made the correction loop obvious.",
    persona: "Family Archivist",
    role: "Home archive maintainer",
    context: "Scanned-photo date restoration",
  },
  {
    quote:
      "Table batching plus inspector checks let us normalize camera and copyright fields before client delivery without touching a CLI script.",
    persona: "Wedding Photographer",
    role: "Independent studio owner",
    context: "Pre-delivery metadata standardization",
  },
  {
    quote:
      "Local-only processing is non-negotiable for unreleased work. Exiftrator gives us visual control while keeping assets on-device.",
    persona: "Studio Ops Lead",
    role: "Post-production coordinator",
    context: "Confidential media workflows",
  },
];

export const OPTIC_PRICING_TIERS: OpticPricingTier[] = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    period: "forever",
    description: "Core workflow for JPEG metadata editing in your browser.",
    features: [
      "Table, Calendar, and Inspector views",
      "Core EXIF editing for JPEG",
      "GPS tagging and map lookup",
      "Undo/redo session history",
    ],
    cta: { label: LAUNCH_EDITOR_CTA, to: "/app" },
  },
  {
    id: "pro",
    name: "Pro",
    badge: "Coming soon",
    price: "$39",
    period: "/year",
    description:
      "For teams that need desktop workflows and advanced reusable configurations.",
    features: [
      "Everything in Free",
      "Desktop app with offline mode",
      "Advanced tags and templates",
      "Preset-based workflows",
    ],
    highlighted: true,
    cta: {
      label: "Join Pro waitlist",
      to: "mailto:hello@exiftrator.com?subject=Exiftrator%20Pro%20Waitlist",
    },
  },
];

export const OPTIC_COMPARISON_COLUMNS: OpticComparisonColumn[] = [
  {
    id: "exiftrator",
    name: "Exiftrator",
    label: "Free / $39yr",
    featured: true,
  },
  { id: "exiftool", name: "ExifTool", label: "Free CLI" },
  { id: "lightroom", name: "Lightroom", label: "$10+/mo" },
  { id: "photomechanic", name: "Photo Mechanic", label: "Paid" },
  { id: "webtools", name: "Single-file Web Tools", label: "Varies" },
];

export const OPTIC_COMPARISON_ROWS: OpticComparisonRow[] = [
  {
    feature: "Visual table batching",
    support: {
      exiftrator: true,
      exiftool: false,
      lightroom: false,
      photomechanic: false,
      webtools: false,
    },
  },
  {
    feature: "Calendar timeline correction",
    support: {
      exiftrator: true,
      exiftool: false,
      lightroom: false,
      photomechanic: false,
      webtools: false,
    },
  },
  {
    feature: "No account required",
    support: {
      exiftrator: true,
      exiftool: true,
      lightroom: false,
      photomechanic: true,
      webtools: true,
    },
  },
  {
    feature: "Local-first workflow",
    support: {
      exiftrator: true,
      exiftool: true,
      lightroom: false,
      photomechanic: false,
      webtools: "partial",
    },
  },
  {
    feature: "Inspector + table + calendar in one app",
    support: {
      exiftrator: true,
      exiftool: false,
      lightroom: false,
      photomechanic: false,
      webtools: false,
    },
  },
  {
    feature: "JPEG write support in browser",
    support: {
      exiftrator: true,
      exiftool: false,
      lightroom: false,
      photomechanic: false,
      webtools: true,
    },
  },
  {
    feature: "RAW / HEIC / TIFF metadata write support",
    support: {
      exiftrator: false,
      exiftool: true,
      lightroom: true,
      photomechanic: true,
      webtools: false,
    },
  },
];

export const OPTIC_FAQ_ITEMS: OpticFaqItem[] = [
  {
    question: "What formats can Exiftrator write metadata to today?",
    answer:
      "Exiftrator currently writes metadata to JPEG files. PNG, TIFF, HEIC, and WebP can be imported for preview and inspection.",
  },
  {
    question: "Are my files uploaded anywhere?",
    answer:
      "No. Editing runs locally in your browser. The only external request is optional location lookup when you use the map search feature.",
  },
  {
    question: "Can I use only one workflow at a time?",
    answer:
      "No. You can move between table, calendar, and inspector workflows in the same editing session.",
  },
  {
    question: "Can I keep my originals untouched?",
    answer:
      "Yes. Exiftrator keeps your originals unchanged and exports updated files separately.",
  },
];

export const OPTIC_PAGE_SECTIONS: LandingContent = {
  blocks: [
    {
      id: "engine",
      type: "hero",
      data: {
        headline: "Exif metadata cleanup at",
        highlight: "production speed.",
        description:
          "Exiftrator is a local-first visual editor for date, GPS, and camera metadata. Clean batches with timeline, calendar, table, and inspector workflows in one place.",
        primaryCta: { label: LAUNCH_EDITOR_CTA, to: "/app" },
        secondaryCta: { label: "Explore workflow blocks", to: "#spotlight" },
        frameLabel: "[ Live editor screenshot ]",
        frameWatermark: "EXIFTRATOR",
        screenshot: {
          light: demoEditorLight,
          dark: demoEditorDark,
          alt: "Exiftrator editor screenshot in light and dark modes.",
        },
      },
    },
    {
      id: "statistics",
      type: "statistics",
      data: {
        items: OPTIC_STATISTICS,
      },
    },
    {
      id: "how-it-works",
      type: "how_it_works",
      data: {
        eyebrow: "How It Works",
        title: "Built for practical day-to-day metadata work",
        steps: OPTIC_HOW_IT_WORKS_STEPS,
      },
    },
    {
      id: "carousel",
      type: "carousel",
      data: {
        eyebrow: "Workflow",
        title: "Move through correction stages without changing tools",
        slides: OPTIC_PRODUCT_CAROUSEL_SLIDES,
      },
    },
    {
      id: "spotlight",
      type: "spotlight",
      data: {
        eyebrow: "Spotlight",
        title: "Built for speed and efficiency",
        description:
          "Four focused workflow blocks: date correction, batch editing, location tagging, and reusable presets.",
        headerAlign: "center",
        items: OPTIC_SPOTLIGHT_ITEMS,
      },
    },
    {
      id: "testimonials",
      type: "testimonials",
      data: {
        eyebrow: "Testimonials",
        title: "Representative outcomes from common use cases",
        description:
          "Composite quotes based on recurring workflows observed in metadata cleanup projects.",
        items: OPTIC_TESTIMONIALS,
      },
    },
    {
      id: "comparison",
      type: "comparison",
      data: {
        eyebrow: "Comparison",
        title: "Where Exiftrator fits",
        description:
          "A metadata-workflow comparison for teams that need visual editing speed while keeping local control.",
        columns: OPTIC_COMPARISON_COLUMNS,
        rows: OPTIC_COMPARISON_ROWS,
        footnote:
          "This matrix compares metadata workflow fit, not full photo-editing suites.",
      },
    },
    {
      id: "pricing",
      type: "pricing",
      data: {
        eyebrow: "Pricing",
        title: "Start free. Upgrade when desktop workflows matter.",
        description:
          "The current product model remains Free + Pro ($39/year, coming soon).",
        tiers: OPTIC_PRICING_TIERS,
      },
    },
    {
      id: "faq",
      type: "faq",
      data: {
        eyebrow: "FAQ",
        title: "Common questions before you start",
        description:
          "Short answers for format support, privacy, and workflow expectations.",
        faqs: OPTIC_FAQ_ITEMS,
      },
    },
    {
      id: "cta",
      type: "cta",
      data: {
        eyebrow: "Launch",
        title: "Start your metadata cleanup now",
        description:
          "Launch the editor and process your files locally with no account required.",
        button: { label: LAUNCH_EDITOR_CTA, to: "/app" },
      },
    },
  ],
};
